
import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, X, Sparkles, LayoutDashboard, Users, History, Mic, BrainCircuit } from 'lucide-react';

interface OnboardingWizardProps {
  onComplete: () => void;
}

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: "Bem-vindo ao Allodos",
      description: "Sua plataforma de inteligência clínica projetada para elevar o nível da sua prática psicoterapêutica através de dados e supervisão automatizada.",
      icon: <BrainCircuit size={48} className="text-indigo-600" />,
      highlight: "Painel"
    },
    {
      title: "Gestão de Pacientes",
      description: "Crie prontuários dinâmicos. Ao preencher a data de nascimento, calculamos a idade automaticamente e você seleciona quais indicadores clínicos deseja acompanhar.",
      icon: <Users size={48} className="text-blue-500" />,
      highlight: "Pacientes"
    },
    {
      title: "Ciclos de Evolução",
      description: "Registre a evolução do seu paciente através de ciclos (Mensais, Trimestrais, etc). A IA analisará os dados e gerará um relatório evolutivo estratégico para cada período.",
      icon: <History size={48} className="text-emerald-500" />,
      highlight: "Gestão de Ciclos"
    },
    {
      title: "Captura de Sessões",
      description: "Grave suas sessões ao vivo ou suba arquivos de áudio externos. Nossa IA fará a transcrição literal fiel, preservando o sigilo total.",
      icon: <Mic size={48} className="text-red-500" />,
      highlight: "Gravações"
    },
    {
      title: "Supervisão IA Especializada",
      description: "Escolha um 'Agente Supervisor' baseado na sua abordagem (TCC, ACT, Psicanálise, etc). A IA extrairá fenômenos clínicos e gerará um plano de manejo estratégico.",
      icon: <Sparkles size={48} className="text-amber-500" />,
      highlight: "Análise IA"
    },
    {
      title: "Documentos e Repositório",
      description: "Acesse modelos de documentos essenciais, como o Termo de Consentimento para Registro Digital, pronto para baixar e utilizar com seus pacientes.",
      icon: <LayoutDashboard size={48} className="text-indigo-400" />,
      highlight: "Repositório"
    }
  ];

  const next = () => {
    if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1);
    else onComplete();
  };

  const prev = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/40 backdrop-blur-md p-4 pointer-events-auto">
      <div className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-[2rem] sm:rounded-[3rem] shadow-2xl border dark:border-slate-800 overflow-hidden animate-in zoom-in-95 duration-300">
        <div className="relative p-5 sm:p-10 pt-16 flex flex-col items-center text-center">
          <button onClick={onComplete} className="absolute top-6 right-6 p-2 text-slate-400 hover:text-slate-600 transition-colors">
            <X size={24} />
          </button>

          <div className="mb-8 p-6 bg-slate-50 dark:bg-slate-800 rounded-[2.5rem] shadow-inner transition-transform hover:scale-105 duration-500">
            {steps[currentStep].icon}
          </div>

          <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-4 tracking-tight">{steps[currentStep].title}</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
            {steps[currentStep].description}
          </p>

          <div className="mt-6 inline-flex items-center gap-2 px-4 py-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full text-[10px] font-black uppercase tracking-widest border border-indigo-100 dark:border-indigo-800">
            Foco: {steps[currentStep].highlight}
          </div>

          <div className="w-full mt-12 flex items-center justify-between">
            <div className="flex gap-1.5">
              {steps.map((_, i) => (
                <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === currentStep ? 'w-8 bg-indigo-600' : 'w-1.5 bg-slate-200 dark:bg-slate-700'}`}></div>
              ))}
            </div>

            <div className="flex gap-3">
              {currentStep > 0 && (
                <button 
                  onClick={prev}
                  className="p-3 text-slate-400 hover:text-indigo-600 transition-all active:scale-90"
                >
                  <ChevronLeft size={24} />
                </button>
              )}
              <button 
                onClick={next}
                className="bg-indigo-600 text-white px-5 sm:px-8 py-3 rounded-2xl font-black uppercase tracking-widest text-xs flex items-center gap-2 shadow-xl shadow-indigo-100 dark:shadow-none hover:bg-indigo-700 transition-all active:scale-95"
              >
                {currentStep === steps.length - 1 ? 'Começar Agora' : 'Próximo'}
                {currentStep !== steps.length - 1 && <ChevronRight size={18} />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
